import { formatMoney } from '@/utils/money';
import { orderTiming, deliveryTravelMinutes } from '@/features/orders/utils/time';
import { paymentPill } from '@/features/orders/utils/payment-pill';
import { paymentMethodIcon, paymentMethodLabel } from '@/features/orders/utils/payment-method';
import LabelChipRow from '@/features/labels/components/LabelChipRow';
import type { Translations } from '@/i18n/locales/en';
import type { OnlineOrder } from '@restaurant/api-client';
import { ListItem } from 'framework7-react';
import OrderBadge from './OrderBadge';

interface OrderCardProps {
  order: OnlineOrder;
  /** Current-locale translations. Passed in (not via useT) because this card is
   *  invoked as a function, not rendered as a component - see Rule 9 below. */
  t: Translations;
  onTap: (order: OnlineOrder) => void;
  onBadgeTap: (order: OnlineOrder, targetEl: HTMLElement) => void;
  /** Highlight when shown alongside a docked inspector (wide split view). */
  isSelected?: boolean;
  /** Preformatted crow-flies distance from the restaurant ("3.2 km") for a
   *  delivery row. Computed by the caller (needs the restaurant origin + locale,
   *  neither reachable from this function-invoked card). Omitted for pickup and
   *  when the restaurant has no pinned location. */
  distanceLabel?: string;
}

/**
 * Order list row. Reuses the shared list-card chrome (`reservation-item*`
 * classes are the workspace's card primitives, not reservation-specific) so
 * the orders list is visually identical to the reservations list. Left column
 * is the time the operator cares about - the scheduled slot when the customer
 * asked for "later", otherwise when the order came in.
 *
 * Returns a `ListItem` directly and is INVOKED as a function at call sites
 * (`OrderCard({...})`, not `<OrderCard/>`). Framework7's `<List>` only wraps a
 * child in its `<ul>` when the child's element type is a literal `ListItem`; a
 * wrapper component is rendered OUTSIDE the `<ul>`, so the default `<li>` bullet
 * marker leaks through. See ReservationCard / FRONTEND_PRACTICES Rule 9. The key
 * lives here since the call site spreads the returned element into a `.map`.
 */
function OrderCard({
  order,
  t,
  onTap,
  onBadgeTap,
  isSelected = false,
  distanceLabel,
}: OrderCardProps) {
  const timing = orderTiming(order, t.orders.timing);
  const travelMin = deliveryTravelMinutes(order);
  const itemCount = order.items.reduce((n, i) => n + i.quantity, 0);
  const pill = paymentPill(order, t.orders.pill);
  const bigColor =
    timing.tone === 'overdue'
      ? '#ff3b30'
      : timing.tone === 'muted'
        ? '#8e8e93'
        : 'var(--f7-theme-color)';
  const smallColor = timing.tone === 'overdue' ? '#ff3b30' : '#8e8e93';

  return (
    <ListItem
      key={order.id}
      noChevron
      className={`reservation-item${isSelected ? ' reservation-item-selected' : ''}`}
    >
      <div
        slot="inner"
        className="reservation-content"
        onClick={() => onTap(order)}
        style={{ cursor: 'pointer' }}
        // Stable handles for journey tests. The card shows the guest's name but
        // never the confirmation code, so a test that asserts on the code passes
        // whether or not the order ever reached the screen — the code lives here
        // instead.
        data-testid="order-card"
        data-order-code={order.confirmationCode}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          {/* Time column - ASAP vs scheduled, same logic as the detail hero. */}
          <div style={{ flexShrink: 0, width: 58 }}>
            <span
              style={{
                fontSize: timing.headlineIsWord ? 15 : 18,
                fontWeight: 700,
                color: bigColor,
                lineHeight: 1.2,
                display: 'block',
              }}
            >
              {timing.headline}
            </span>
            {/* Sub-chip only when it carries live timing ("8m" / "scheduled" /
                "overdue"). Muted (completed / cancelled) rows would just show the
                month ("JUL"), which is noise next to the time - so it's dropped. */}
            {timing.tone !== 'muted' && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: timing.tone === 'overdue' ? 700 : 400,
                  color: smallColor,
                }}
              >
                {timing.chip}
              </span>
            )}
          </div>

          {/* Info column */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Name owns the full line so it isn't squeezed by the total - the
                total lives one line down (right-aligned) instead. */}
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                lineHeight: 1.25,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {order.customerName}
            </div>
            {/* Triage labels - the operator's own signal, so it sits above the
                low-priority meta. Capped at 3 + a counter so the row height stays
                constant all the way down the list. */}
            <LabelChipRow labels={order.labels ?? []} />
            {/* Meta line - low-priority reference details only. The total lives in
                the right rail (under the status button), so nothing here competes
                with it or with the name. */}
            <div
              style={{
                marginTop: 3,
                fontSize: 12,
                color: '#8e8e93',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '2px 8px',
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                <i className="f7-icons" style={{ fontSize: 13 }}>
                  {order.type === 'PICKUP' ? 'bag' : 'car_fill'}
                </i>
                {order.type === 'PICKUP' ? t.orders.type.PICKUP : t.orders.type.DELIVERY}
              </span>
              <span>{t.orders.itemCount(itemCount)}</span>
              {distanceLabel && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <i className="f7-icons" style={{ fontSize: 13 }}>
                    location
                  </i>
                  {/* Distance + drive time together so the owner reads "how far"
                      and "how long the leg takes" in one glance. The drive time is
                      dropped on terminal (completed / cancelled) rows — where
                      `timing.tone` is muted — since it's no longer actionable. */}
                  {travelMin != null && timing.tone !== 'muted'
                    ? `${distanceLabel} · ${t.orders.acceptSheet.minutes(travelMin)}`
                    : distanceLabel}
                </span>
              )}
            </div>

            {/* Payment line - the rail (Cash / Card on handoff / Bank transfer /
                Card), with the status pill appended only when it carries something
                worth surfacing (paymentPill returns null for the expected states:
                pending in-person collection and cancelled-void). */}
            {order.paymentMethodKind && (
              <div
                style={{
                  marginTop: 2,
                  fontSize: 12,
                  color: '#8e8e93',
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '2px 8px',
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <i className="f7-icons" style={{ fontSize: 13 }}>
                    {paymentMethodIcon(order.paymentMethodKind)}
                  </i>
                  {paymentMethodLabel(
                    order.paymentMethodKind,
                    t.orders.methodKind,
                    order.paymentMethodId,
                  )}
                </span>
                {pill && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '1px 6px',
                      borderRadius: 6,
                      background: pill.bg,
                      color: pill.color,
                    }}
                  >
                    {pill.label}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Right rail - status button with the total stacked beneath it, both
              centred on the same axis. Keeping the two "outcome" facts (what
              state, how much) in one column reads cleanly down the list. A min
              width holds the column constant across sections (a fit-content
              "Pending" is narrower than "Completed"), so the column never
              shifts. */}
          <div
            style={{
              flexShrink: 0,
              alignSelf: 'flex-start',
              minWidth: 100,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <OrderBadge
              status={order.status}
              onClick={(e) => {
                e.stopPropagation();
                onBadgeTap(order, e.currentTarget as HTMLElement);
              }}
            />
            <div
              style={{
                // Span the button's width and centre, so the total sits squarely
                // UNDER the status button rather than flush-right beside it.
                alignSelf: 'stretch',
                textAlign: 'center',
                fontSize: 15,
                fontWeight: 700,
                // A steady, legible neutral for every row - money is a fact you
                // always want to read; colour meaning lives in the status badge
                // and the payment pill, not here.
                color: '#1c1c1e',
                lineHeight: 1.2,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatMoney(order.totalCents, order.currency)}
            </div>
          </div>
        </div>

        {order.customerNote && (
          <div
            style={{
              marginTop: 8,
              padding: '6px 8px',
              borderRadius: 8,
              background: '#fffbeb',
              border: '0.5px solid #fde68a',
              display: 'flex',
              alignItems: 'baseline',
              gap: 6,
            }}
          >
            <i className="f7-icons" style={{ fontSize: 12, color: '#d97706', flexShrink: 0 }}>
              exclamationmark_circle_fill
            </i>
            <span style={{ fontSize: 12, color: '#92400e', lineHeight: 1.4 }}>
              {order.customerNote}
            </span>
          </div>
        )}
      </div>
    </ListItem>
  );
}

export default OrderCard;
